import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  Typography,
  Switch,
  FormControlLabel,
  Grid,
  Card,
  CardMedia,
  IconButton,
  CircularProgress
} from '@mui/material';
import { Delete, Add } from '@mui/icons-material';
import { adminService } from '../../services/adminService';

const AdModal = ({ open, onClose, onSuccess, ad = null }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    images: [],
    video: '',
    targetUrl: '',
    facebookUrl: '',
    instagramUrl: '',
    snapchatUrl: '',
    tiktokUrl: '',
    youtubeUrl: '',
    startDate: '',
    endDate: '',
    isActive: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  useEffect(() => {
    if (open) {
      if (ad) {
        // Gérer la migration depuis imageUrl vers images
        const images = ad.images && Array.isArray(ad.images) 
          ? ad.images 
          : (ad.imageUrl ? [ad.imageUrl] : []);
        
        setFormData({
          title: ad.title || '',
          description: ad.description || '',
          images: images || '',
          video: ad.video || '',
          targetUrl: ad.targetUrl || '',
          facebookUrl: ad.facebookUrl || '',
          instagramUrl: ad.instagramUrl || '',
          snapchatUrl: ad.snapchatUrl || '',
          tiktokUrl: ad.tiktokUrl || '',
          youtubeUrl: ad.youtubeUrl || '',
          startDate: ad.startDate ? new Date(ad.startDate).toISOString().split('T')[0] : '',
          endDate: ad.endDate ? new Date(ad.endDate).toISOString().split('T')[0] : '',
          isActive: ad.isActive !== false
        });
      } else {
        setFormData({
          title: '',
          description: '',
          images: [],
          video: '',
          targetUrl: '',
          facebookUrl: '',
          instagramUrl: '',
          snapchatUrl: '',
          tiktokUrl: '',
          youtubeUrl: '',
          startDate: new Date().toISOString().split('T')[0],
          endDate: '',
          isActive: true
        });
      }
      setError('');
    }
  }, [ad, open]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    // Vérifier le nombre total d'images (max 5)
    const currentImagesCount = formData.images.length;
    if (currentImagesCount + files.length > 5) {
      setError(`Maximum 5 images autorisées. Vous avez déjà ${currentImagesCount} image(s).`);
      event.target.value = '';
      return;
    }

    // Vérifier chaque fichier
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        setError('Seules les images (JPEG, JPG, PNG, GIF, WEBP) sont autorisées');
        event.target.value = '';
        return;
      }

      if (file.size > 50 * 1024 * 1024) {
        setError('Chaque fichier ne doit pas dépasser 50MB');
        event.target.value = '';
        return;
      }
    }

    setError('');
    setUploadingImages(true);

    try {
      const response = await adminService.uploadAdImages(files);
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...response.imageUrls]
      }));
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'upload des images');
    } finally {
      setUploadingImages(false);
      event.target.value = '';
    }
  };

  const removeImage = async (imageUrl, index) => {
    // Supprimer l'image du serveur si elle a été uploadée
    if (imageUrl && imageUrl.includes('/imgAnnonce/')) {
      try {
        const filename = imageUrl.split('/').pop();
        await adminService.deleteAdImage(filename);
      } catch (err) {
        console.error('Erreur lors de la suppression de l\'image:', err);
      }
    }

    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleVideoChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Vérifier le type de fichier
    const allowedTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime', 'video/x-msvideo'];
    if (!allowedTypes.includes(file.type)) {
      setError('Seules les vidéos (MP4, WEBM, OGG, MOV, AVI) sont autorisées');
      event.target.value = '';
      return;
    }

    // Vérifier la taille (max 15MB)
    if (file.size > 15 * 1024 * 1024) {
      setError('La vidéo ne doit pas dépasser 15MB');
      event.target.value = '';
      return;
    }

    setError('');
    setUploadingVideo(true);

    try {
      const response = await adminService.uploadAdVideo(file);
      setFormData(prev => ({
        ...prev,
        video: response.videoUrl
      }));
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'upload de la vidéo');
    } finally {
      setUploadingVideo(false);
      event.target.value = '';
    }
  };

  const removeVideo = async () => {
    // Supprimer la vidéo du serveur si elle a été uploadée
    if (formData.video && formData.video.includes('/videoAnnonce/')) {
      try {
        const filename = formData.video.split('/').pop();
        await adminService.deleteAdVideo(filename);
      } catch (err) {
        console.error('Erreur lors de la suppression de la vidéo:', err);
      }
    }

    setFormData(prev => ({
      ...prev,
      video: ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validation
      if (!formData.title.trim()) {
        setError('Le titre est requis');
        setLoading(false);
        return;
      }

      // if (!formData.images || formData.images.length === 0) {
      //   setError('Au moins une image est requise');
      //   setLoading(false);
      //   return;
      // }

      if (ad) {
        await adminService.updateAd(ad.id, formData);
        onSuccess('Annonce modifiée avec succès');
      } else {
        await adminService.createAd(formData);
        onSuccess('Annonce créée avec succès');
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {ad ? 'Modifier l\'annonce' : 'Créer une annonce'}
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} md={12}>
              <TextField
                name="title"
                label="Titre *"
                value={formData.title}
                onChange={handleChange}
                fullWidth
                required
                margin="normal"
              />

              <TextField
                name="description"
                label="Description"
                value={formData.description}
                onChange={handleChange}
                fullWidth
                multiline
                rows={3}
                margin="normal"
              />

              <Box sx={{ mt: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6">Images de l'annonce * (max 5)</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formData.images.length} / 5
                  </Typography>
                </Box>
                
                {formData.images.length > 0 && (
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    {formData.images.map((imageUrl, index) => (
                      <Grid item xs={6} sm={4} key={index}>
                        <Card sx={{ position: 'relative' }}>
                          <CardMedia
                            component="img"
                            height="140"
                            image={imageUrl}
                            alt={`Image ${index + 1}`}
                            sx={{ objectFit: 'cover' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => removeImage(imageUrl, index)}
                            disabled={uploadingImages}
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              bgcolor: 'rgba(255, 255, 255, 0.9)',
                              '&:hover': { bgcolor: 'white' }
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
                
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                  disabled={uploadingImages || formData.images.length >= 5}
                  style={{ display: 'none' }}
                />
                <label htmlFor="image-upload">
                  <Button
                    component="span"
                    variant="outlined"
                    disabled={uploadingImages || formData.images.length >= 5}
                    startIcon={uploadingImages ? <CircularProgress size={20} /> : <Add />}
                    fullWidth
                  >
                    {uploadingImages 
                      ? 'Upload en cours...' 
                      : formData.images.length >= 5 
                        ? 'Maximum 5 images atteint' 
                        : formData.images.length > 0 
                          ? 'Ajouter des images' 
                          : 'Sélectionner des images'}
                  </Button>
                </label>
                
                <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                  Formats acceptés: JPEG, JPG, PNG, GIF, WEBP (max 50MB par image - sera compressé en WebP, maximum 5 images)
                </Typography>
              </Box>

              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Vidéo de l'annonce (optionnel)</Typography>
                
                {formData.video && (
                  <Box sx={{ mb: 2, position: 'relative' }}>
                    <Card sx={{ position: 'relative' }}>
                      <CardMedia
                        component="video"
                        height="200"
                        src={formData.video}
                        controls
                        sx={{ 
                          width: '100%',
                          maxHeight: 200,
                          objectFit: 'contain',
                          bgcolor: '#000'
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={removeVideo}
                        disabled={uploadingVideo}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          bgcolor: 'rgba(255, 255, 255, 0.9)',
                          '&:hover': { bgcolor: 'white' }
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Card>
                  </Box>
                )}
                
                <input
                  id="video-upload"
                  type="file"
                  accept="video/*"
                  onChange={handleVideoChange}
                  disabled={uploadingVideo}
                  style={{ display: 'none' }}
                />
                <label htmlFor="video-upload">
                  <Button
                    component="span"
                    variant="outlined"
                    disabled={uploadingVideo}
                    startIcon={uploadingVideo ? <CircularProgress size={20} /> : <Add />}
                    fullWidth
                  >
                    {uploadingVideo 
                      ? 'Upload en cours...' 
                      : formData.video 
                        ? 'Remplacer la vidéo' 
                        : 'Sélectionner une vidéo'}
                  </Button>
                </label>
                
                <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                  Formats acceptés: MP4, WEBM, OGG, MOV, AVI (max 15MB)
                </Typography>
              </Box>

              <TextField
                name="targetUrl"
                label="URL de destination"
                value={formData.targetUrl}
                onChange={handleChange}
                fullWidth
                margin="normal"
                placeholder="https://example.com"
              />

              <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                Réseaux sociaux (optionnel)
              </Typography>

              <TextField
                name="facebookUrl"
                label="Facebook"
                value={formData.facebookUrl}
                onChange={handleChange}
                fullWidth
                margin="normal"
                placeholder="https://facebook.com/..."
              />

              <TextField
                name="instagramUrl"
                label="Instagram"
                value={formData.instagramUrl}
                onChange={handleChange}
                fullWidth
                margin="normal"
                placeholder="https://instagram.com/..."
              />

              <TextField
                name="snapchatUrl"
                label="Snapchat"
                value={formData.snapchatUrl}
                onChange={handleChange}
                fullWidth
                margin="normal"
                placeholder="https://snapchat.com/..."
              />

              <TextField
                name="tiktokUrl"
                label="TikTok"
                value={formData.tiktokUrl}
                onChange={handleChange}
                fullWidth
                margin="normal"
                placeholder="https://tiktok.com/@..."
              />

              <TextField
                name="youtubeUrl"
                label="YouTube"
                value={formData.youtubeUrl}
                onChange={handleChange}
                fullWidth
                margin="normal"
                placeholder="https://youtube.com/..."
              />

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  <TextField
                    name="startDate"
                    label="Date de début"
                    type="date"
                    value={formData.startDate}
                    onChange={handleChange}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    name="endDate"
                    label="Date de fin"
                    type="date"
                    value={formData.endDate}
                    onChange={handleChange}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>

              <FormControlLabel
                control={
                  <Switch
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                  />
                }
                label="Annonce active"
                sx={{ mt: 2 }}
              />
            </Grid>
{/* 
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>
                Aperçu de l'image
              </Typography>
              
              {(imagePreview || formData.imageUrl) ? (
                <Card sx={{ maxWidth: 300 }}>
                  <CardMedia
                    component="img"
                    height="200"
                    image={imagePreview || formData.imageUrl}
                    alt="Aperçu de l'annonce"
                    sx={{ objectFit: 'cover' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </Card>
              ) : (
                <Box
                  sx={{
                    width: 300,
                    height: 200,
                    border: '2px dashed #ccc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.secondary'
                  }}
                >
                  Aucune image
                </Box>
              )}

              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Formats acceptés: JPEG, JPG, PNG, GIF, WEBP (max 50MB - sera compressé en WebP)
              </Typography>

              {uploadingImage && (
                <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                  Upload en cours...
                </Typography>
              )}
            </Grid> */}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Sauvegarde...' : (ad ? 'Modifier' : 'Créer')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AdModal;
